import React, { useEffect, useState } from "react";
import '../styles/ProgressDialog.css'

import { useFetch } from "../hooks/useFetch";

import closeIcon from '../images/close-icon.svg'
import StudentInfo from "./StudentInfo";
import ProgressSelector from "./ProgressSelector";
import ProgressDeadline from "./CommonProgressDeadline";
import ProgressRecord from "./ProgressRecord";
import { CommonGoalType } from "../components/CommonGoal";

import axios from "axios";
import { useParams } from "react-router-dom";
import ProgressDialogSkeleton from "./ProgressDialogSkeleton";
import LinearProgressBar from "./LinearProgressBar";
import { useCookies } from "react-cookie";
import SnackbarAlert from "./SnackbarAlert";
import ConfirmDialog from "./ConfirmDialog";

interface ProgressDialogProps {
    handleClickClose: any;
    handleBackDropClick: any;
    id: number;
    commonGoalList: any[];
    openEditInfo: boolean;
    setOpenEditInfo: (value: boolean) => void;
}
interface CommonProgress {
    id: number,
    commonGoalId: number,
    studentId: string,
    isCompleted: number,
    note: string,
    lastUpdate: string,
    staff: string,
    fileName1: string,
    fileDesc1: string,
    fileName2: string,
    fileDesc2: string,
    fileName3: string,
    fileDesc3: string,
}

export default function CommonProgressDialog({ id, commonGoalList, handleClickClose, handleBackDropClick, openEditInfo, setOpenEditInfo }: ProgressDialogProps) {

    const { studentID } = useParams()
    const [cookies, setCookie] = useCookies(['user'])
    const [progressID, setProgressID] = useState(id);
    const [note, setNote] = useState("");

    const [filesFromServer, setFilesFromServer] = useState<{
        fileNo: number,
        fileName: string,
        fileDesc: string
    }[]>([])

    const [emptyFileSlots, setEmptyFileSlots] = useState<number[]>([])

    const [files, setFiles] = useState<File[]>([]);
    const [filesDescription, setFilesDescription] = useState<string[]>([]);

    const [isCompleted, setIsCompleted] = useState(false);
    const [changeCount, setChangeCount] = useState(0);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    const [fetchLoading, setFetchLoading] = useState(false);
    const [loading, setLoading] = useState(false);

    const [progress, setProgress] = useState<CommonProgress>();
    const [selectedGoal, setSelectedGoal] = useState<number>();
    const [goal, setGoal] = useState<CommonGoalType>();

    const [snackbarMessage, setSnackbarMessage] = useState<null | {
        message: string,
        severity?: 'success' | 'info' | 'warning' | 'error',
        autoHideDuration?: number | null
    }>(null)

    const { data: getProgressData, status: getProgressStatus, error: getProgressError, loading: getProgressLoading } = useFetch({
        url: `/CommonProgresses/${progressID}`,
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${cookies?.user?.token}`
        }
    }, progressID)

    const { data: getProgressListData, status: getProgressListStatus, error: getProgressListError, loading: getProgressListLoading } = useFetch({
        url: `/CommonProgresses?studentId=${studentID}`,
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${cookies?.user?.token}`
        }
    }, selectedGoal)

    useEffect(() => {
        if (studentID === undefined) {
            handleClickClose()
        }
    }, [studentID])

    useEffect(() => {
        if (getProgressData && commonGoalList) {
            setProgress(getProgressData)

            setSelectedGoal(getProgressData.commonGoalId)
            setGoal(commonGoalList.find((goal) => goal?.id === getProgressData.commonGoalId))
            setIsCompleted(getProgressData.isCompleted)
            setNote(getProgressData.note)

            setFilesFromServer([]) // reset files
            setEmptyFileSlots([]) // reset empty slots
            if (getProgressData.fileName1) {
                setFilesFromServer((prevFiles) => [...prevFiles, {
                    fileNo: 1,
                    fileName: getProgressData.fileName1,
                    fileDesc: getProgressData.fileDesc1
                }])
            } else setEmptyFileSlots((prevSlots) => [...prevSlots, 1])

            if (getProgressData.fileName2) {
                setFilesFromServer((prevFiles) => [...prevFiles, {
                    fileNo: 2,
                    fileName: getProgressData.fileName2,
                    fileDesc: getProgressData.fileDesc2
                }])
            } else setEmptyFileSlots((prevSlots) => [...prevSlots, 2])

            if (getProgressData.fileName3) {
                setFilesFromServer((prevFiles) => [...prevFiles, {
                    fileNo: 3,
                    fileName: getProgressData.fileName3,
                    fileDesc: getProgressData.fileDesc3
                }])
            } else setEmptyFileSlots((prevSlots) => [...prevSlots, 3])

            if (getProgressData.fileName4) {
                setFilesFromServer((prevFiles) => [...prevFiles, {
                    fileNo: 4,
                    fileName: getProgressData.fileName4,
                    fileDesc: getProgressData.fileDesc4
                }])
            } else setEmptyFileSlots((prevSlots) => [...prevSlots, 4])

            if (getProgressData.fileName5) {
                setFilesFromServer((prevFiles) => [...prevFiles, {
                    fileNo: 5,
                    fileName: getProgressData.fileName5,
                    fileDesc: getProgressData.fileDesc5
                }])
            } else setEmptyFileSlots((prevSlots) => [...prevSlots, 5])

            if (getProgressData.fileName6) {
                setFilesFromServer((prevFiles) => [...prevFiles, {
                    fileNo: 6,
                    fileName: getProgressData.fileName6,
                    fileDesc: getProgressData.fileDesc6
                }])
            } else setEmptyFileSlots((prevSlots) => [...prevSlots, 6])

            if (getProgressData.fileName7) {
                setFilesFromServer((prevFiles) => [...prevFiles, {
                    fileNo: 7,
                    fileName: getProgressData.fileName7,
                    fileDesc: getProgressData.fileDesc7
                }])
            } else setEmptyFileSlots((prevSlots) => [...prevSlots, 7])

            if (getProgressData.fileName8) {
                setFilesFromServer((prevFiles) => [...prevFiles, {
                    fileNo: 8,
                    fileName: getProgressData.fileName8,
                    fileDesc: getProgressData.fileDesc8
                }])
            } else setEmptyFileSlots((prevSlots) => [...prevSlots, 8])

            if (getProgressData.fileName9) {
                setFilesFromServer((prevFiles) => [...prevFiles, {
                    fileNo: 9,
                    fileName: getProgressData.fileName9,
                    fileDesc: getProgressData.fileDesc9
                }])
            } else setEmptyFileSlots((prevSlots) => [...prevSlots, 9])

            if (getProgressData.fileName10) {
                setFilesFromServer((prevFiles) => [...prevFiles, {
                    fileNo: 10,
                    fileName: getProgressData.fileName10,
                    fileDesc: getProgressData.fileDesc10
                }])
            } else setEmptyFileSlots((prevSlots) => [...prevSlots, 10])

            setTimeout(() => {
                setFetchLoading(false)
            }, 300)
            setChangeCount(0)
        }
    }, [getProgressData, commonGoalList])

    const createNewProgress = async (goalId: number) => {
        setSnackbarMessage(null)
        await axios.post(`/CommonProgresses/${goalId}`, {
            studentId: studentID,
            staffId: cookies.user.staffId,
        },
            {
                headers: {
                    'Authorization': `Bearer ${cookies?.user?.token}`
                },
                withCredentials: true
            })
            .then((response) => {
                setProgressID(response.data.id);
            })
            .catch((error) => {
                setSnackbarMessage({
                    message: "Creating new progress failed.",
                    severity: "error",
                    autoHideDuration: 3000
                })
            });
    }

    const handleOnChangeSelector = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (getProgressListData) {
            setFetchLoading(true);
            const newProgress = getProgressListData.find((progress: CommonProgress) => progress.commonGoalId === Number(e.target.value));
            if (newProgress) {
                setProgressID(newProgress.id);
            } else {
                // create new progress if not exist
                createNewProgress(Number(e.target.value))
            }
        }
    }

    const handleOnNoteChange = (event: any) => {
        setNote(event.target.value);
    }

    const handleOnFileChange = (event: any) => {
        setSnackbarMessage(null)
        if (filesFromServer.length + files.length + event.target.files.length > 10) {
            setSnackbarMessage({
                message: "You can only upload 10 files",
                severity: "warning",
                autoHideDuration: 3000
            })
            return
        }
        const checkFileSize = [...event.target.files].every((file) => file.size < 25_000_000)
        if (checkFileSize) {
            setFiles((prevFiles) => [...prevFiles, ...event.target.files])
            setFilesDescription((prevFilesDescription) => [...prevFilesDescription, ...Array(event.target.files.length).fill("")])
        } else {
            setSnackbarMessage({
                message: "File size must be less than 25MB",
                severity: "warning",
                autoHideDuration: 3000
            })
        }
    }

    const handleOnFileDelete = (index: number) => {
        setFiles(files.filter((_, i) => i !== index));
        setFilesDescription(filesDescription.filter((_, i) => i !== index));
    }

    const handleOnFileDescriptionChange = (event: any, index: number) => {
        setFilesDescription((prevFilesDescription) => {
            const newFilesDescription = [...prevFilesDescription];
            newFilesDescription[index] = event.target.value;
            return newFilesDescription;
        });
    }

    const handleOnDeleteFileFromServer = (fileNo: number) => {
        setEmptyFileSlots((prevEmptyFileSlots) => [...prevEmptyFileSlots, fileNo])
        setFilesFromServer(filesFromServer.filter((file) => file.fileNo !== fileNo))
    }

    const handleOnFileFromServerDescriptionChange = (event: any, fileNo: number) => {
        setFilesFromServer((prevFilesFromServer) => {
            const newFilesFromServer = [...prevFilesFromServer];
            const file = newFilesFromServer.find((file) => file.fileNo === fileNo)
            if (file) {
                file.fileDesc = event.target.value;
            }
            return newFilesFromServer;
        });
    }

    const handleOnDownloadFileFromServer = (fileNo: number) => {
        const file = filesFromServer.find((file) => file.fileNo === fileNo)
        if (file) {
            setSnackbarMessage(null)
            axios.get(`/CommonProgresses/${progressID}/files/${fileNo}`, {
                headers: {
                    'Authorization': `Bearer ${cookies?.user?.token}`
                },
                responseType: 'blob',
            }).then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]))
                let link = document.createElement('a')
                link.setAttribute('style', 'display: none')
                link.href = url
                link.setAttribute('target', '_blank')
                link.setAttribute('download', file.fileName)
                link.click()
            }).catch((error) => {
                setSnackbarMessage({    
                    message: "Failed to download file! Please try again later",
                    severity: "error",
                    autoHideDuration: 3000
                })
            });
        }
    }

    useEffect(() => {
        setChangeCount((prev) => prev + 1)
    }, [note, files, filesDescription, isCompleted, emptyFileSlots])

    const handleClickCancel = () => {
        if (changeCount > 1) {
            setOpenConfirmDialog(true)
        }else {
            handleClickClose()
        }
    }

    const handleClickSave = async () => {
        setLoading(true);
        setSnackbarMessage(null)
        if (progressID) {
            await axios.put(`/CommonProgresses/${progressID}`, {
                note: note,
                isCompleted: isCompleted,
                commonGoalId: selectedGoal,
                studentId: studentID,
                staffId: cookies.user.staffId,
            },
                {
                    headers: {
                        'Authorization': `Bearer ${cookies?.user?.token}`
                    },
                    withCredentials: true
                })
                .then((response) => {
                    
                })
                .catch((error) => {
                    setSnackbarMessage({
                        message: error.response.status === 404 ? "Progress not found" : "Failed to save progress",
                        severity: "error",
                        autoHideDuration: 3000
                    })
                    setLoading(false)
                    return
                });

            const formData = new FormData();

            filesFromServer.forEach(({ fileNo, fileName, fileDesc }) => {
                formData.append(`FileName${fileNo}`, fileName)
                if (fileDesc) formData.append(`FileDesc${fileNo}`, fileDesc)
            })

            files.forEach((file, index) => {
                const fileNo = emptyFileSlots[index]
                formData.append(`FileName${fileNo}`, file.name)
                formData.append(`FileDesc${fileNo}`, filesDescription[index])
                formData.append(`File${fileNo}`, file)
                setEmptyFileSlots(emptyFileSlots.filter((slot) => slot !== fileNo))
            })


            await axios.post(`/CommonProgresses/${progressID}/files`, formData, {
                headers: {
                    'Authorization': `Bearer ${cookies?.user?.token}`
                },
                withCredentials: true
            }).then((response) => {
                
                setSnackbarMessage({
                    message: "Progress saved",
                    severity: "success",
                    autoHideDuration: 1500
                })
                setTimeout(() => {
                    setFiles([]);
                    setFilesDescription([])
                    setChangeCount(0)
                    setLoading(false)
                    handleClickClose()
                }, 2000)
            }).catch((error) => {
                setSnackbarMessage({
                    message: error.response.status === 404 ? "Progress not found" : "Failed to save progress",
                    severity: "error",
                    autoHideDuration: 3000
                })
                setLoading(false)
            })

        }
    }

    return (
        <div className="ProgressDialog" onClick={handleBackDropClick} >
            <ConfirmDialog open={openConfirmDialog} setOpen={setOpenConfirmDialog} title="Discard changes?" content="Are you sure you want to discard your changes?" handleConfirm={handleClickClose} confirmLabel="Discard"/>
            <div className="ProgressDialog-dialog">
                <button className="ProgressDialog-close-button" onClick={handleClickCancel}>
                    <img src={closeIcon} width={24} height={24} alt="close-icon" />
                </button>
                {
                    (fetchLoading || getProgressLoading || getProgressListLoading || !goal || loading || progress?.studentId != studentID) &&
                    <div className="ProgressDialog-linear-progress">
                        <LinearProgressBar />
                    </div>
                }
                {studentID && <StudentInfo id={studentID} openEditInfo={openEditInfo} setOpenEditInfo={setOpenEditInfo} />}
                {
                    (fetchLoading || getProgressLoading || getProgressListLoading) || (progress?.studentId != studentID || !goal || !studentID) ? <ProgressDialogSkeleton /> : (
                        <>
                            {
                                commonGoalList &&
                                <ProgressSelector
                                    commonGoalId={progress?.commonGoalId || -1}
                                    isCompleted={isCompleted}
                                    selectedGoal={selectedGoal || -1}
                                    handleOnChangeSelector={handleOnChangeSelector}
                                    goalDescription={goal?.description || ''}
                                    commonGoalList={commonGoalList}
                                />
                            }
                            <ProgressDeadline
                                deadline={goal?.deadline || ''}
                                isCompleted={isCompleted}
                            />
                            <ProgressRecord
                                isCompleted={isCompleted}
                                setIsCompleted={setIsCompleted}
                                note={note}
                                files={files}
                                filesDescription={filesDescription}
                                filesFromServer={filesFromServer}
                                handleOnNoteChange={handleOnNoteChange}
                                handleOnFileChange={handleOnFileChange}
                                handleOnFileDelete={handleOnFileDelete}
                                handleOnDeleteFileFromServer={handleOnDeleteFileFromServer}
                                handleOnDownloadFileFromServer={handleOnDownloadFileFromServer}
                                handleOnFileDescriptionChange={handleOnFileDescriptionChange}
                                handleOnFileFromServerDescriptionChange={handleOnFileFromServerDescriptionChange}
                                isScholarship={false}
                                loading={loading}
                            />

                            <div className="ProgressDialog-bottom-container">
                                {progress?.lastUpdate && progress?.staff &&
                                    <div className="ProgressDialog-last-update">
                                        <p>Last modified: </p>
                                        <p>{progress.lastUpdate} โดย {progress.staff}</p>
                                    </div>
                                }
                                <div className="ProgressDialog-actions">
                                    <button className="ProgressDialog-button" onClick={handleClickCancel}>Cancel</button>
                                    <button className="ProgressDialog-button" onClick={() => handleClickSave()} disabled={loading}>Save</button>
                                </div>
                            </div>
                        </>
                    )
                }

            </div>
            {
                (snackbarMessage) &&
                <SnackbarAlert severity={snackbarMessage.severity} message={snackbarMessage.message} />
            }
        </div>

    )
}